import React from 'react';
import { Button, makeStyles, mergeClasses, shorthands } from '@fluentui/react-components';
import { ReactComponent as AgentSourcesIcon } from '../../../../../assets/toolkit/agent-sources.svg';
import { customColors, customFonts } from '../../../../../styles';
import useDismissOnOutsideClick from '../../../hooks/useDismissOnOutsideClick';
import type { KnowledgeSourceAgentOption } from '../../../hooks/useKnowledgeSources';
import KnowledgeSourcesMenu from './KnowledgeSourcesMenu';

const trayButtonIcon = '& .toolkitTrayIcon';
const trayButtonIconSvg = '& .toolkitTrayIcon svg';

const useStyles = makeStyles({
    knowledgeSourcesWrapper: {
        position: 'relative',
    },
    trayButton: {
        height: '32px',
        fontFamily: customFonts.Lato,
        fontSize: '14px',
        fontWeight: '700',
        color: customColors.protivitiDarkBlue,
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('0px', '12px'),
        backgroundColor: customColors.white,
        whiteSpace: 'nowrap',
        transition: 'all 0.2s ease',

        [trayButtonIconSvg]: {
            color: customColors.protivitiDarkBlue,
            transition: 'all 0.2s ease',
        },

        '&:hover': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
            ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),

            [trayButtonIcon]: {
                backgroundColor: 'transparent',
            },

            [trayButtonIconSvg]: {
                color: customColors.white,
            },
        },

        '&:active': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
            transition: 'all 0s',

            [trayButtonIcon]: {
                backgroundColor: 'transparent',
            },

            [trayButtonIconSvg]: {
                color: customColors.white,
            },
        },
    },
    trayButtonActive: {
        backgroundColor: customColors.protivitiDarkBlue,
        color: customColors.white,

        [trayButtonIconSvg]: {
            color: customColors.white,
        },

        '&:hover': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
        },
    },
});

interface KnowledgeSourcesTrayItemProps {
    isOpen: boolean;
    isDropUp: boolean;
    agents: KnowledgeSourceAgentOption[];
    selectedAgentIds: string[];
    areAllSelected: boolean;
    onToggleOpen: () => void;
    onClose: () => void;
    onToggleSelectedAgent: (agentId: string) => void;
    onToggleAllAgents: () => void;
}

const KnowledgeSourcesTrayItem: React.FC<KnowledgeSourcesTrayItemProps> = ({
    isOpen,
    isDropUp,
    agents,
    selectedAgentIds,
    areAllSelected,
    onToggleOpen,
    onClose,
    onToggleSelectedAgent,
    onToggleAllAgents,
}) => {
    const styles = useStyles();
    const containerRef = useDismissOnOutsideClick<HTMLDivElement>(isOpen, onClose);

    return (
        <div ref={containerRef} className={styles.knowledgeSourcesWrapper}>
            <Button
                className={mergeClasses(styles.trayButton, isOpen && styles.trayButtonActive)}
                appearance="transparent"
                icon={<AgentSourcesIcon className="toolkitTrayIcon" />}
                onClick={onToggleOpen}
            >
                Knowledge Sources
            </Button>
            {isOpen && (
                <KnowledgeSourcesMenu
                    isDropUp={isDropUp}
                    agents={agents}
                    selectedAgentIds={selectedAgentIds}
                    areAllSelected={areAllSelected}
                    onToggleSelectedAgent={onToggleSelectedAgent}
                    onToggleAllAgents={onToggleAllAgents}
                />
            )}
        </div>
    );
};

export default KnowledgeSourcesTrayItem;
